import { coerceDate } from '@/utils/dateKeys';
import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';

export type TripDateRangeSource = 'trip' | 'destinations' | 'itinerary' | 'none';

export type TripDateRange = {
  start: Date | null;
  end: Date | null;
  source: TripDateRangeSource;
};

const normalizeDay = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

const minDate = (dates: Date[]): Date => dates.reduce((a, b) => (b.getTime() < a.getTime() ? b : a));
const maxDate = (dates: Date[]): Date => dates.reduce((a, b) => (b.getTime() > a.getTime() ? b : a));

const diffDays = (a: Date, b: Date): number => {
  const ms = normalizeDay(b).getTime() - normalizeDay(a).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

export function deriveTripDateRange(
  trip: Partial<Trip> | null | undefined,
  items?: ItineraryItem[] | null,
): TripDateRange {
  const tripStartRaw = coerceDate(trip?.startDate ?? null);
  const tripEndRaw = coerceDate(trip?.endDate ?? null);

  const destDates: Date[] = [];
  const destinations = (trip as any)?.destinations as Array<{ startDate?: unknown; endDate?: unknown }> | undefined;
  if (Array.isArray(destinations)) {
    for (const d of destinations) {
      const ds = coerceDate(d?.startDate ?? null);
      const de = coerceDate(d?.endDate ?? null);
      if (ds) destDates.push(ds);
      if (de) destDates.push(de);
    }
  }

  const itemDates: Date[] = [];
  if (Array.isArray(items)) {
    for (const it of items) {
      const d = coerceDate((it as any)?.startDate ?? null);
      if (d) itemDates.push(d);
      const e = coerceDate((it as any)?.endDate ?? null);
      if (e) itemDates.push(e);
    }
  }

  const hasTripStart = !!tripStartRaw;
  const hasTripEnd = !!tripEndRaw;

  // If the stored trip range spans multiple days, treat it as the source of truth.
  if (tripStartRaw && tripEndRaw && diffDays(tripStartRaw, tripEndRaw) >= 1) {
    return { start: normalizeDay(tripStartRaw), end: normalizeDay(tripEndRaw), source: 'trip' };
  }

  // Otherwise, extend/fill using destinations + itinerary items.
  const candidates: Date[] = [];
  if (tripStartRaw) candidates.push(tripStartRaw);
  if (tripEndRaw) candidates.push(tripEndRaw);
  candidates.push(...destDates);
  candidates.push(...itemDates);

  if (candidates.length === 0) {
    return { start: null, end: null, source: 'none' };
  }

  const start = normalizeDay(minDate(candidates));
  const end = normalizeDay(maxDate(candidates));
  const safeEnd = end.getTime() < start.getTime() ? start : end;

  // Prefer a meaningful source label for debugging/UX.
  const usedDestinations =
    destDates.length > 0 &&
    (minDate(destDates).getTime() <= start.getTime() || maxDate(destDates).getTime() >= safeEnd.getTime());
  const usedItinerary =
    itemDates.length > 0 &&
    (minDate(itemDates).getTime() <= start.getTime() || maxDate(itemDates).getTime() >= safeEnd.getTime());

  const source: TripDateRangeSource =
    usedDestinations ? 'destinations' : usedItinerary ? 'itinerary' : hasTripStart || hasTripEnd ? 'trip' : 'none';

  return { start, end: safeEnd, source };
}

export function expandDateRangeToDays(
  start: Date | null | undefined,
  end: Date | null | undefined,
  maxDays = 400,
): Date[] {
  if (!start) return [];
  const safeStart = normalizeDay(start);
  const safeEnd = end ? normalizeDay(end) : safeStart;
  const capEnd = safeEnd.getTime() < safeStart.getTime() ? safeStart : safeEnd;

  const out: Date[] = [];
  const cursor = new Date(safeStart);
  let guard = 0;
  while (cursor.getTime() <= capEnd.getTime() && guard < maxDays) {
    out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return out;
}

